import React, { useEffect, useMemo, useState } from 'react'
import propTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import CreatableSelect from 'react-select/creatable'
import { Typography, makeStyles, useTheme } from '@material-ui/core'
import { loadTags, createTag } from '../../../../store/contentAssetTags/contentAssetTagsActions'
import { buildReactSelectStyles } from '../wizardFieldStyles'

const useStyles = makeStyles((theme) => ({
	wrapper: {
		marginBottom: theme.spacing(2)
	},
	label: {
		fontSize: '0.75rem',
		marginBottom: theme.spacing(0.5)
	},
	error: {
		marginTop: theme.spacing(0.5)
	}
}))

const TagsCreatable = ({ selectedTags, onAddTag, onRemoveTag }) => {
	const classes = useStyles()
	const theme = useTheme()
	const dispatch = useDispatch()
	const tags = useSelector((state) => state.contentAssetTags.get('tags').toJS())
	const isLoading = useSelector((state) => state.contentAssetTags.get('isLoading'))
	const isCreating = useSelector((state) => state.contentAssetTags.get('isCreating'))
	const [createError, setCreateError] = useState(null)

	useEffect(() => {
		dispatch(loadTags())
	}, [dispatch])

	const options = useMemo(() => tags.map((t) => ({ value: t.Id, label: t.Name, raw: t })), [tags])
	const selected = useMemo(() => selectedTags.map((t) => ({ value: t.Id, label: t.Name, raw: t })), [selectedTags])

	const handleChange = (newValue, actionMeta) => {
		setCreateError(null)
		if (!newValue) newValue = []
		// Detect removal: items present in `selected` but not in `newValue`.
		const removed = selectedTags.filter((t) => !newValue.some((nv) => nv.value === t.Id))
		removed.forEach((t) => onRemoveTag(t.Id))
		// Add any new options that already had a value (existing tags only).
		newValue.forEach((nv) => {
			if (typeof nv.value === 'number' && nv.raw) {
				if (!selectedTags.some((t) => t.Id === nv.value)) onAddTag(nv.raw)
			}
		})
	}

	const handleCreate = async (rawInput) => {
		setCreateError(null)
		try {
			const tag = await dispatch(createTag(rawInput))
			if (tag && !selectedTags.some((t) => t.Id === tag.Id)) {
				onAddTag(tag)
			}
		} catch (err) {
			setCreateError('Could not create tag. Try again.')
		}
	}

	return (
		<div className={classes.wrapper}>
			<Typography className={classes.label}>Tags (click to add)</Typography>
			<CreatableSelect
				isMulti
				placeholder='Add or search tags...'
				options={options}
				value={selected}
				onChange={handleChange}
				onCreateOption={handleCreate}
				isLoading={isLoading || isCreating}
				styles={buildReactSelectStyles(theme, false)}
				menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
			/>
			{createError && (
				<Typography variant='caption' color='error' className={classes.error}>
					{createError}
				</Typography>
			)}
		</div>
	)
}

TagsCreatable.propTypes = {
	selectedTags: propTypes.array.isRequired,
	onAddTag: propTypes.func.isRequired,
	onRemoveTag: propTypes.func.isRequired
}

export default TagsCreatable
